import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Check, Pencil, Plus, X } from "lucide-react";
import {
  MAX_VARIANT_DIMENSIONS,
  VARIANT_NAME_KEY,
  addVariantDimension,
  buildVariantOptionRows,
  createVariantFromOptionRow,
  initialVariantDimensions,
  removeVariantDimension,
  renameVariantOption,
  variantOptionsLabel,
  variantsNeedDimensionDefault,
  type VariantDimension,
  type VariantOptionRow,
} from "@KassaroAB/shared/catalog/variantOptions";
import { BarcodeType } from "@KassaroAB/shared/types/enums/BarcodeType";
import type { ExtraProductField } from "@KassaroAB/shared/types/extraProductField";
import type { ProductVariantId, StoreId } from "@KassaroAB/shared/types/ids";
import type { ProductVariant } from "@KassaroAB/shared/types/productVariant";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { borderRadius, spacing } from "@styling/sizing";
import { generateBarcodeApi } from "../../../api/barcodesApi";
import { Button } from "../../../components/Button";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { FormActions } from "../../../components/FormActions";
import { InfoBox } from "../../../components/InfoBox";
import { Modal } from "../../../components/Modal";
import { CellInput } from "./MoneyTableCell";

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  margin-bottom: ${spacing.x3}px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: ${fontSizes.medium}px;
  font-weight: 600;
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const Chip = styled.button<{ $selected?: boolean; $new?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.x05}px;
  padding: ${spacing.x05}px ${spacing.x1}px ${spacing.x05}px ${spacing.x2}px;
  border: 1px solid
    ${({ $selected, $new }) =>
      $selected || $new ? colors.primary : colors.border};
  border-radius: ${borderRadius.large}px;
  background-color: ${({ $selected }) =>
    $selected ? colors.infoBackground : colors.backgroundLight};
  color: ${colors.text};
  font-size: ${fontSizes.medium}px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ValueChip = styled(Chip).attrs({ as: "span" })`
  cursor: default;
`;

const ChipIcon = styled.button`
  display: inline-flex;
  padding: 2px;
  border: none;
  border-radius: ${borderRadius.small}px;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;

  &:hover {
    background-color: ${colors.backgroundDark};
    color: ${colors.text};
  }
`;

const InlineForm = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const TextInput = styled(CellInput)`
  width: 220px;
  border-color: ${colors.border};
  background-color: ${colors.backgroundLight};
`;

const DimensionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  background-color: ${colors.backgroundLight};
`;

const Rows = styled.div`
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  background-color: ${colors.backgroundLight};
`;

const RowLabel = styled.label<{ $muted: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  padding: ${spacing.x05}px ${spacing.x2}px;
  border-bottom: 1px solid ${colors.border};
  font-size: ${fontSizes.medium}px;
  color: ${({ $muted }) => ($muted ? colors.textMuted : colors.text)};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

const RowValues = styled.span`
  flex: 1;
`;

const RowStatus = styled.span<{ $tone: "new" | "removed" | "muted" }>`
  font-size: ${fontSizes.small}px;
  color: ${({ $tone }) =>
    $tone === "new"
      ? colors.primary
      : $tone === "removed"
        ? colors.danger
        : colors.textMuted};
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  font-size: ${fontSizes.medium}px;
  cursor: pointer;
`;

type PendingInput =
  | { kind: "dimensionDefault"; key: string }
  | { kind: "rename"; key: string; value: string };

interface VariantOptionsModalProps {
  variants: ProductVariant[];
  extraFields: ExtraProductField[];
  storeId: StoreId | null;
  barcodeType: BarcodeType;
  canGenerateBarcodes: boolean;
  hasStock: (variant: ProductVariant) => boolean;
  onSave: (variants: ProductVariant[]) => void;
  onClose: () => void;
}

export const VariantOptionsModal = ({
  variants: initialVariants,
  extraFields,
  storeId,
  barcodeType,
  canGenerateBarcodes,
  hasStock,
  onSave,
  onClose,
}: VariantOptionsModalProps) => {
  const { t } = useTranslation();
  const extraFieldKeys = useMemo(
    () => extraFields.map((field) => field.key),
    [extraFields],
  );
  const selectableKeys = [...extraFieldKeys, VARIANT_NAME_KEY];
  const [workingVariants, setWorkingVariants] =
    useState<ProductVariant[]>(initialVariants);
  const [dimensions, setDimensions] = useState<VariantDimension[]>(() =>
    initialVariantDimensions(initialVariants, extraFieldKeys),
  );
  const [newValues, setNewValues] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<PendingInput | null>(null);
  const [pendingText, setPendingText] = useState("");
  const [uncheckedVariantIds, setUncheckedVariantIds] = useState<string[]>([]);
  const [toggledNewRows, setToggledNewRows] = useState<string[]>([]);
  const [generateBarcodes, setGenerateBarcodes] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Barcodes already allocated by the server, keyed by row, so a retry after
  // a partial failure reuses them instead of allocating new ones.
  const generatedBarcodes = useRef(
    new Map<string, NonNullable<ProductVariant["barcode"]>>(),
  );

  const dimensionLabel = (key: string) =>
    key === VARIANT_NAME_KEY
      ? t("productForm.variantOptions.productName")
      : (extraFields.find((field) => field.key === key)?.name ?? key);

  const { rows, otherVariants } = useMemo(
    () =>
      buildVariantOptionRows({
        variants: workingVariants,
        dimensions,
        uncheckedVariantIds,
        toggledNewRows,
      }),
    [workingVariants, dimensions, uncheckedVariantIds, toggledNewRows],
  );
  const checkedRows = rows.filter((row) => row.checked);
  const removedVariants = rows
    .filter((row) => row.existing && !row.checked)
    .map((row) => row.existing!);

  const addDimension = (key: string, defaultValue: string | null) => {
    const added = addVariantDimension(
      workingVariants,
      initialVariants,
      key,
      defaultValue,
    );
    setWorkingVariants(added.variants);
    setDimensions((previous) => [...previous, added.dimension]);
    setToggledNewRows([]);
  };

  const toggleDimension = (key: string) => {
    if (dimensions.some((dimension) => dimension.key === key)) {
      setWorkingVariants((previous) =>
        removeVariantDimension(previous, initialVariants, key),
      );
      setDimensions((previous) =>
        previous.filter((dimension) => dimension.key !== key),
      );
      setToggledNewRows([]);
      return;
    }
    if (variantsNeedDimensionDefault(workingVariants, initialVariants, key)) {
      setPending({ kind: "dimensionDefault", key });
      setPendingText("");
      return;
    }
    addDimension(key, null);
  };

  const addValue = (key: string) => {
    const value = (newValues[key] ?? "").trim();
    if (!value) return;
    setNewValues((previous) => ({ ...previous, [key]: "" }));
    setDimensions((previous) =>
      previous.map((dimension) =>
        dimension.key === key &&
        !dimension.values.some((item) => item.value === value)
          ? {
              ...dimension,
              values: [...dimension.values, { value, isNew: true }],
            }
          : dimension,
      ),
    );
  };

  const removeValue = (key: string, value: string) =>
    setDimensions((previous) =>
      previous.map((dimension) =>
        dimension.key === key
          ? {
              ...dimension,
              values: dimension.values.filter((item) => item.value !== value),
            }
          : dimension,
      ),
    );

  const submitPending = () => {
    if (pending?.kind === "dimensionDefault") {
      addDimension(pending.key, pendingText.trim() || null);
    } else if (pending?.kind === "rename") {
      const renamed = renameVariantOption(
        { variants: workingVariants, dimensions, toggledNewRows },
        pending.key,
        pending.value,
        pendingText,
      );
      if (renamed) {
        setWorkingVariants(renamed.variants);
        setDimensions(renamed.dimensions);
        setToggledNewRows(renamed.toggledNewRows);
      }
    }
    setPending(null);
  };

  const toggleRow = (row: VariantOptionRow) => {
    if (row.existing) {
      const variantId = row.existing.id;
      setUncheckedVariantIds((previous) =>
        previous.includes(variantId)
          ? previous.filter((id) => id !== variantId)
          : [...previous, variantId],
      );
      return;
    }
    setToggledNewRows((previous) =>
      previous.includes(row.comboKey)
        ? previous.filter((key) => key !== row.comboKey)
        : [...previous, row.comboKey],
    );
  };

  const createVariant = (row: VariantOptionRow) =>
    createVariantFromOptionRow(
      row,
      dimensions,
      crypto.randomUUID() as ProductVariantId,
    );

  const save = async () => {
    setConfirmRemove(false);
    setError(null);
    const newRows = checkedRows.filter((row) => !row.existing);
    let created: ProductVariant[];
    if (generateBarcodes && canGenerateBarcodes && storeId) {
      setIsSaving(true);
      try {
        created = [];
        for (const row of newRows) {
          let barcode = generatedBarcodes.current.get(row.key);
          if (!barcode) {
            const { barcode: code } = await generateBarcodeApi(storeId);
            barcode = { code, type: barcodeType, format: null };
            generatedBarcodes.current.set(row.key, barcode);
          }
          created.push({ ...createVariant(row), barcode });
        }
      } catch {
        setError(t("productForm.variantOptions.generateBarcodesFailed"));
        return;
      } finally {
        setIsSaving(false);
      }
    } else {
      created = newRows.map(createVariant);
    }
    const createdByKey = new Map(
      newRows.map((row, index) => [row.key, created[index]!]),
    );
    onSave([
      ...checkedRows.map((row) => row.existing ?? createdByKey.get(row.key)!),
      ...otherVariants,
    ]);
  };

  const rowStatus = (row: VariantOptionRow) =>
    !row.existing
      ? row.checked
        ? { tone: "new" as const, text: t("productForm.variantOptions.new") }
        : {
            tone: "muted" as const,
            text: t("productForm.variantOptions.skipped"),
          }
      : row.checked
        ? {
            tone: "muted" as const,
            text: t("productForm.variantOptions.existing"),
          }
        : {
            tone: "removed" as const,
            text: t("productForm.variantOptions.willBeRemoved"),
          };

  const pendingForm = (label: string, placeholder?: string) => (
    <InlineForm>
      <TextInput
        autoFocus
        aria-label={label}
        value={pendingText}
        placeholder={placeholder}
        onChange={(event) => setPendingText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            submitPending();
          } else if (event.key === "Escape") {
            event.stopPropagation();
            setPending(null);
          }
        }}
      />
      <ChipIcon type="button" title={t("common.save")} onClick={submitPending}>
        <Check size={16} />
      </ChipIcon>
      <ChipIcon
        type="button"
        title={t("common.cancel")}
        onClick={() => setPending(null)}
      >
        <X size={16} />
      </ChipIcon>
    </InlineForm>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t("productForm.variantOptions.title")}
      maxWidth={720}
    >
      <Section>
        <SectionTitle>
          {t("productForm.variantOptions.dimensions")}
        </SectionTitle>
        <Hint>
          {t("productForm.variantOptions.dimensionsHint", {
            max: MAX_VARIANT_DIMENSIONS,
          })}
        </Hint>
        <Chips>
          {selectableKeys.map((key) => {
            const selected = dimensions.some(
              (dimension) => dimension.key === key,
            );
            return (
              <Chip
                key={key}
                type="button"
                $selected={selected}
                aria-pressed={selected}
                disabled={
                  !selected && dimensions.length >= MAX_VARIANT_DIMENSIONS
                }
                onClick={() => toggleDimension(key)}
              >
                {dimensionLabel(key)}
                {selected ? <X size={14} /> : <Plus size={14} />}
              </Chip>
            );
          })}
        </Chips>
        {pending?.kind === "dimensionDefault" && (
          <DimensionCard>
            <SectionTitle>
              {t("productForm.variantOptions.existingVariantsValue", {
                fieldName: dimensionLabel(pending.key),
              })}
            </SectionTitle>
            {pendingForm(
              dimensionLabel(pending.key),
              t("productForm.variantOptions.existingVariantsValuePlaceholder"),
            )}
          </DimensionCard>
        )}
      </Section>

      {dimensions.length > 0 && (
        <Section>
          {dimensions.map((dimension) => (
            <DimensionCard key={dimension.key}>
              <SectionTitle>{dimensionLabel(dimension.key)}</SectionTitle>
              <Chips>
                {dimension.values.map((item) =>
                  pending?.kind === "rename" &&
                  pending.key === dimension.key &&
                  pending.value === item.value ? (
                    <span key={item.value}>
                      {pendingForm(t("productForm.variantOptions.renameValue"))}
                    </span>
                  ) : (
                    <ValueChip key={item.value} $new={item.isNew}>
                      {item.value}
                      <ChipIcon
                        type="button"
                        title={t("productForm.variantOptions.renameValue")}
                        onClick={() => {
                          setPending({
                            kind: "rename",
                            key: dimension.key,
                            value: item.value,
                          });
                          setPendingText(item.value);
                        }}
                      >
                        <Pencil size={14} />
                      </ChipIcon>
                      <ChipIcon
                        type="button"
                        title={t("productForm.variantOptions.removeValue")}
                        onClick={() => removeValue(dimension.key, item.value)}
                      >
                        <X size={14} />
                      </ChipIcon>
                    </ValueChip>
                  ),
                )}
              </Chips>
              <InlineForm>
                <TextInput
                  aria-label={t("productForm.variantOptions.newValue")}
                  value={newValues[dimension.key] ?? ""}
                  placeholder={t(
                    "productForm.variantOptions.newValuePlaceholder",
                  )}
                  onChange={(event) =>
                    setNewValues((previous) => ({
                      ...previous,
                      [dimension.key]: event.target.value,
                    }))
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addValue(dimension.key);
                    }
                  }}
                />
                <Button
                  type="button"
                  $small
                  $variant="outlined"
                  $color="secondary"
                  disabled={!(newValues[dimension.key] ?? "").trim()}
                  onClick={() => addValue(dimension.key)}
                >
                  {t("productForm.variantOptions.addValue")}
                </Button>
              </InlineForm>
            </DimensionCard>
          ))}
        </Section>
      )}

      {rows.length > 0 && (
        <Section>
          <SectionTitle>
            {t("productForm.variantOptions.selectedCount", {
              count: checkedRows.length,
            })}
          </SectionTitle>
          <Rows>
            {rows.map((row) => {
              const status = rowStatus(row);
              return (
                <RowLabel key={row.key} $muted={!row.checked}>
                  <input
                    type="checkbox"
                    checked={row.checked}
                    onChange={() => toggleRow(row)}
                  />
                  <RowValues>{row.values.join(" / ")}</RowValues>
                  <RowStatus $tone={status.tone}>{status.text}</RowStatus>
                </RowLabel>
              );
            })}
          </Rows>
        </Section>
      )}

      {otherVariants.length > 0 && (
        <Section>
          <SectionTitle>
            {t("productForm.variantOptions.otherVariants")}
          </SectionTitle>
          <Hint>{t("productForm.variantOptions.otherVariantsHint")}</Hint>
          <Rows>
            {otherVariants.map((variant) => (
              <RowLabel as="div" key={variant.id} $muted={false}>
                <RowValues>
                  {variantOptionsLabel(variant, extraFieldKeys) ??
                    t("productForm.variantsTable.variant")}
                </RowValues>
              </RowLabel>
            ))}
          </Rows>
        </Section>
      )}

      {canGenerateBarcodes && (
        <Section>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={generateBarcodes}
              onChange={(event) => setGenerateBarcodes(event.target.checked)}
            />
            {t("productForm.variantOptions.generateBarcodes")}
          </CheckboxRow>
        </Section>
      )}

      {error && (
        <Section>
          <InfoBox variant="error">{error}</InfoBox>
        </Section>
      )}

      <FormActions>
        <Button
          type="button"
          $variant="outlined"
          $color="secondary"
          onClick={onClose}
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="button"
          disabled={isSaving}
          onClick={() =>
            removedVariants.length > 0 ? setConfirmRemove(true) : save()
          }
        >
          {t("productForm.variantOptions.apply")}
        </Button>
      </FormActions>

      <ConfirmDialog
        isOpen={confirmRemove}
        title={t("productForm.variantOptions.removeTitle")}
        message={t(
          removedVariants.some(hasStock)
            ? "productForm.variantOptions.removeWithStockMessage"
            : "productForm.variantOptions.removeMessage",
          { count: removedVariants.length },
        )}
        confirmLabel={t("productForm.variantOptions.apply")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={save}
        onCancel={() => setConfirmRemove(false)}
      />
    </Modal>
  );
};
